import {
  Box,
  Button,
  ButtonDropdown,
  Cards,
  Container,
  Header,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  Textarea,
  FormField,
} from '@amzn/awsui-components-react';
import { clone } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLSE } from '../../../store/lse.context';
import { LSEMessage } from '@/src/types/Lse';
import { ConfirmModal } from '@/src/components/common/ConfirmModal';
import { AuthRole } from '@/src/constants/shared';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { isValidUrl } from '@/src/utils/validation.utils';

const MAX_TEXT_LENGTH = 1000;
const MAX_HEADER_LENGTH = 250;

const SELECTABLE_AUTH_ROLES = {
  [AuthRole.GUEST_USER]: {
    value: AuthRole.GUEST_USER,
    label: AuthRole.GUEST_USER,
  },
  [AuthRole.EVENT_SUPPORT]: {
    value: AuthRole.EVENT_SUPPORT,
    label: AuthRole.EVENT_SUPPORT,
  },
  [AuthRole.CHALLENGE_BUILDER]: {
    value: AuthRole.CHALLENGE_BUILDER,
    label: AuthRole.CHALLENGE_BUILDER,
  },
  [AuthRole.SBTS_LEARNER]: {
    value: AuthRole.SBTS_LEARNER,
    label: AuthRole.SBTS_LEARNER,
  },
  [AuthRole.SUBSCRIBED_USER]: {
    value: AuthRole.SUBSCRIBED_USER,
    label: AuthRole.SUBSCRIBED_USER,
  },
  [AuthRole.JAM_ADMIN]: {
    value: AuthRole.JAM_ADMIN,
    label: AuthRole.JAM_ADMIN,
  },
};

const getOptionForRole = (role: AuthRole) => {
  switch (role) {
    case AuthRole.GUEST_USER:
    case AuthRole.SUBSCRIBED_USER:
    case AuthRole.EVENT_SUPPORT:
    case AuthRole.CHALLENGE_BUILDER:
    case AuthRole.JAM_ADMIN:
    case AuthRole.SBTS_LEARNER:
      return SELECTABLE_AUTH_ROLES[role];
    default:
      return {
        value: role,
        label: role,
      };
  }
};

const GlobalNotifications: React.FC = () => {
  const [selectedNotification, setSelectedNotification] = useState<LSEMessage | undefined | null>();
  const [workflow, setWorkflow] = useState<'update' | 'delete' | 'create' | ''>('');
  const [loading, setLoading] = useState(false);
  const { globalNotifications, createGlobalNotification, deactivateLSE, updateLSE, loadGlobalNotifications } = useLSE();
  const { t } = useTranslation();
  const textLengthValid =
    ['update', 'create'].includes(workflow) && (selectedNotification?.text?.length || 0) <= MAX_TEXT_LENGTH;
  const headerLengthValid =
    ['update', 'create'].includes(workflow) && (selectedNotification?.header?.length || 0) <= MAX_HEADER_LENGTH;
  const urlValid = !selectedNotification?.url || isValidUrl(selectedNotification.url);

  return (
    <SpaceBetween direction="vertical" size="m">
      <Container
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="s">
                <Button
                  onClick={() => {
                    setSelectedNotification(new LSEMessage());
                    setWorkflow('create');
                  }}
                >
                  {t(i18nKeys.settings.globalNotifications.buttons.createNew)}
                </Button>
              </SpaceBetween>
            }
          >
            {t(i18nKeys.settings.globalNotifications.title)}
          </Header>
        }
        footer={
          <Box padding="l">
            <SpaceBetween size="s" direction="horizontal">
              {!!workflow && (
                <Button onClick={() => setWorkflow('')}>
                  {t(i18nKeys.settings.globalNotifications.buttons.reset)}
                </Button>
              )}
              {!!['update', 'create'].includes(workflow) && (
                <Button
                  variant="primary"
                  disabled={!textLengthValid || !headerLengthValid || !urlValid}
                  loading={loading}
                  onClick={() => {
                    if (workflow === 'update') {
                      setLoading(true);
                      updateLSE(
                        Object.assign(new LSEMessage(), {
                          header: selectedNotification?.header,
                          text: selectedNotification?.text,
                          level: selectedNotification?.level,
                          url: selectedNotification?.url || undefined,
                          audience: selectedNotification?.audience,
                        }),
                        selectedNotification?.id || undefined,
                        'global-notification'
                      )
                        .then(() => {
                          setSelectedNotification(null);
                          setWorkflow('');
                        })
                        .catch((err) => {
                          // eslint-disable-next-line no-console
                          console.error(err);
                        })
                        .finally(() => setLoading(false));
                    } else if (workflow === 'create') {
                      if (!!selectedNotification) {
                        const normalizedNotification = Object.assign(clone(selectedNotification), {
                          url: selectedNotification?.url || undefined,
                        });
                        setLoading(true);
                        createGlobalNotification(
                          normalizedNotification,
                          'global-notification',
                          selectedNotification?.audience
                        )
                          .then(() => {
                            setSelectedNotification(null);
                            setWorkflow('');
                          })
                          .catch((err) => {
                            // eslint-disable-next-line no-console
                            console.error(err);
                          })
                          .finally(() => setLoading(false));
                      }
                    }
                  }}
                >
                  {workflow === 'update' && <>{t(i18nKeys.settings.globalNotifications.buttons.updateNotification)}</>}
                  {workflow === 'create' && <>{t(i18nKeys.settings.globalNotifications.buttons.createNotification)}</>}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
      >
        {!!selectedNotification && ['update', 'create'].includes(workflow || '') && (
          <SpaceBetween direction="vertical" size="m">
            <FormField
              label={t(i18nKeys.settings.globalNotifications.labels.header)}
              characterCountText={
                headerLengthValid ? `${selectedNotification?.header?.length ?? 0}/${MAX_HEADER_LENGTH}` : ''
              }
              errorText={!headerLengthValid ? `${selectedNotification?.header?.length ?? 0}/${MAX_HEADER_LENGTH}` : ''}
            >
              <Input
                value={selectedNotification?.header || ''}
                onChange={({ detail }) =>
                  setSelectedNotification(Object.assign(clone(selectedNotification), { header: detail.value }))
                }
              />
            </FormField>
            <FormField
              label={t(i18nKeys.settings.globalNotifications.labels.text)}
              characterCountText={
                textLengthValid ? `${selectedNotification?.text?.length ?? 0}/${MAX_TEXT_LENGTH}` : ''
              }
              errorText={!textLengthValid ? `${selectedNotification?.text?.length ?? 0}/${MAX_TEXT_LENGTH}` : ''}
            >
              <Textarea
                value={selectedNotification?.text || ''}
                rows={6}
                onChange={({ detail }) =>
                  setSelectedNotification(Object.assign(clone(selectedNotification), { text: detail.value }))
                }
              />
            </FormField>
            <FormField
              label={t(i18nKeys.settings.globalNotifications.labels.url)}
              description={t(i18nKeys.settings.globalNotifications.descriptions.urlOptional)}
              errorText={!urlValid ? t(i18nKeys.settings.lse.labels.invalidURL) : ''}
            >
              <Input
                value={selectedNotification.url || ''}
                onChange={({ detail }) =>
                  setSelectedNotification(Object.assign(clone(selectedNotification), { url: detail.value }))
                }
              />
            </FormField>
            <FormField label={t(i18nKeys.settings.globalNotifications.labels.level)}>
              <Select
                options={['warning', 'info', 'success', 'error'].map((value) => ({ value }))}
                selectedOption={{ value: selectedNotification.level ?? undefined }}
                onChange={({ detail }) =>
                  setSelectedNotification(
                    Object.assign(clone(selectedNotification), { level: detail.selectedOption.value })
                  )
                }
              />
            </FormField>
            <FormField label={t(i18nKeys.settings.globalNotifications.labels.audience)}>
              <Multiselect
                selectedOptions={selectedNotification.audience?.map((role) => getOptionForRole(role)) || []}
                onChange={({ detail }) =>
                  setSelectedNotification(
                    Object.assign(clone(selectedNotification), {
                      audience: detail.selectedOptions.map((so) => so.value),
                    })
                  )
                }
                options={Object.values(SELECTABLE_AUTH_ROLES)}
                placeholder={t(i18nKeys.settings.globalNotifications.placeholder.audience)}
              />
            </FormField>
          </SpaceBetween>
        )}
        {!!globalNotifications?.length && (
          <Box padding="m">
            <Cards
              items={globalNotifications?.map((item) => ({
                name: item.header ?? item.text ?? '',
                description: item.header ? item.text : item?.url,
                id: item?.id,
              }))}
              cardDefinition={{
                header: (item) => (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignContent: 'start',
                    }}
                  >
                    <span>{item.name}</span>
                    <ButtonDropdown
                      onItemClick={({ detail }) => {
                        const [_, notificationId, action] = detail.id.split('__');
                        const item = globalNotifications.find((gn) => gn.id === notificationId);
                        if (!!item && (action === 'update' || action === 'delete')) {
                          setWorkflow(action);
                          setSelectedNotification(clone(item));
                        }
                      }}
                      items={[
                        {
                          text: t(i18nKeys.general.edit) || 'Edit',
                          id: `notification__${item.id}__update`,
                          disabled: false,
                        },
                        {
                          text: t(i18nKeys.general.delete) || 'Delete',
                          id: `notification__${item.id}__delete`,
                          disabled: false,
                        },
                      ]}
                      ariaLabel={t(i18nKeys.settings.globalNotifications.ariaLabels.controlInstance)}
                      variant="inline-icon"
                    />
                  </div>
                ),
                sections: [
                  {
                    id: 'description',
                    header: t(i18nKeys.settings.globalNotifications.cardSections.description),
                    content: (item) => (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignContent: 'start',
                        }}
                      >
                        <span>{item.description}</span>
                      </div>
                    ),
                  },
                ],
              }}
            />
          </Box>
        )}
        <ConfirmModal
          visible={workflow === 'delete'}
          title={t(i18nKeys.settings.globalNotifications.modals.disableNotification)}
          onCancel={() => {
            setWorkflow('');
            setSelectedNotification(null);
          }}
          onDismiss={() => {
            setWorkflow('');
            setSelectedNotification(null);
          }}
          onConfirm={() => {
            if (selectedNotification?.id) {
              deactivateLSE(selectedNotification.id)
                .then(() => {
                  loadGlobalNotifications();
                  setSelectedNotification(null);
                  setWorkflow('');
                })
                .catch((err) => {
                  // eslint-disable-next-line no-console
                  console.error(err);
                });
            }
          }}
        />
      </Container>
    </SpaceBetween>
  );
};

export default GlobalNotifications;
